import { Component, ComponentFactory, ComponentFactoryResolver, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faTerminal } from '@fortawesome/pro-duotone-svg-icons';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { ComponentHostDirective } from 'src/app/components/general-directives/component-host.directive';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { BrickLocationType } from 'src/app/samwebDescription/samweb/brickLocationType';
import { UpdateDynamicActionsBrickConfiguration } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/dynamic-actions-behavior/dynamic-actions-behavior.actions';
import { DynamicActionsBehaviorStateSelectors } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/dynamic-actions-behavior/dynamic-actions-behavior.selectors';
import { DynamicActionsBrickSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/dynamic-actions-brick-settings/dynamic-actions-brick-settings.state';
import { DynamicActionsBrickLayoutComponentResolver } from './dynamic-actions-brick-layouts/dynamic-actions-brick-layout-component-resolver';
import { IDynamicActionsBrickLayoutComponent } from './dynamic-actions-brick-layouts/i-dynamic-actions-brick-layout-component';
import { DynamicActionsBrickSettingsComponent } from './dynamic-actions-brick-settings/dynamic-actions-brick-settings.component';

@Component({
   selector: 'samweb-dynamic-actions-brick',
   templateUrl: './dynamic-actions-brick.component.html',
   styleUrls: ['./dynamic-actions-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'dynamicActionsBrick',
   brickTitleTranslationKey: 'brick.brick_definitions.dynamic-actions-brick.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.dynamic-actions-brick.description',
   brickIcon: faTerminal,
   minColWidth: 10,
   minRowHeight: 5,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: DynamicActionsBrickSettingsComponent,
   settingStateHandler: DynamicActionsBrickSettingsState,
   allowedBrickLocation: BrickLocationType.BRICKWALL,
})
export class DynamicActionsBrickComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   @ViewChild(ComponentHostDirective, { static: true })
   public layoutHost: ComponentHostDirective;
   public brickInstanceId: Readonly<string>;

   private destroy$ = new Subject();

   constructor(private store: Store, private componentFactoryResolver: ComponentFactoryResolver) {
      super();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit(): void {
      this.store
         .select(DynamicActionsBehaviorStateSelectors.getSpecificType)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            distinctUntilChanged(),
            tap((layoutType) => {
               const layoutFactory: ComponentFactory<IDynamicActionsBrickLayoutComponent> = this.componentFactoryResolver.resolveComponentFactory(
                  DynamicActionsBrickLayoutComponentResolver.fromLayoutTypeEnum(layoutType)
               );
               try {
                  this.layoutHost.viewContainerRef.clear();
               } catch {}
               const component = this.layoutHost.viewContainerRef.createComponent<IDynamicActionsBrickLayoutComponent>(layoutFactory);
               component.instance.brickInstanceId = this.brickInstanceId;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(DynamicActionsBehaviorStateSelectors.getSpecificJsonConfigurationWithReplacedTagsFn)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            distinctUntilChanged(),
            switchMap((jsonConfiguration: string) => this.store.dispatch(new UpdateDynamicActionsBrickConfiguration(this.brickInstanceId, jsonConfiguration))),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }
}
